import React, { useCallback } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WAWidgetSizeToOverviewSize } from "../../WidgetAdd/utils/WAWidgetSizeToOverviewSize";
import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";

import { overviewWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/overviewWidgetSizes";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { View } from "react-native";
import { WAFakeHeader } from "../../WidgetAdd/WAFakeHeader";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { WAIntegrationFlourishPraiseQuery } from "./__generated__/WAIntegrationFlourishPraiseQuery.graphql";
import { WAIntegrationFlourishPraiseMutation } from "./__generated__/WAIntegrationFlourishPraiseMutation.graphql";
import { WidgetIntegrationFlourishPraise } from "widgets/src/WidgetIntegrationFlourishPraise/WidgetIntegrationFlourishPraise";

type Props =
  UserLoggedInNavigatorScreenProps<"WidgetAddIntegrationFlourishPraise">;

export const WAFlourishPraise = withWebModal(WAFlourishPraiseC, true);
function WAFlourishPraiseC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWAFlourishPraise {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWAFlourishPraise(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WAIntegrationFlourishPraiseQuery>(
    graphql`
      query WAIntegrationFlourishPraiseQuery(
        $storecastId: ID!
        $input: PreviewWidgetFlourishPraiseIntegrationInput!
      ) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetFlourishPraiseIntegration(input: $input) {
            ...WidgetIntegrationFlourishPraise_widgetIntegrationFlourishPraise
          }
        }
      }
    `,
    {
      storecastId: props.route.params.storecastId,
      input: {
        size: "TWO_X_FOUR",
      },
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [createWidget, loading] =
    useMutation<WAIntegrationFlourishPraiseMutation>(graphql`
      mutation WAIntegrationFlourishPraiseMutation(
        $storecastId: ID!
        $input: CreateWidgetIntegrationFlourishPraiseInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetIntegrationFlourishPraise(widget: $input) {
            id
          }
        }
      }
    `);

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      createWidget({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            positionX:
              props.route.params.position.x + props.route.params.position.c * 2,
            positionY: props.route.params.position.y,
            size: WAWidgetSizeToOverviewSize(size),
          },
        },
        onCompleted: () => props.navigation.pop(2),
      });
    },
    [
      createWidget,
      props.navigation,
      props.route.params.position.c,
      props.route.params.position.x,
      props.route.params.position.y,
      props.route.params.storecastId,
    ]
  );

  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  return (
    <View style={{ flex: 1 }}>
      {isCompact ? null : <WAFakeHeader />}
      <WASizePreview
        fullScreen
        loading={loading}
        maxSize={props.route.params.maxSize}
        onCreate={onCreate}
        unitSize={props.route.params.unitSize}
        widgetSizes={overviewWidgetSizes}
      >
        {data.dashboard.previewWidgetFlourishPraiseIntegration ? (
          <WidgetIntegrationFlourishPraise
            widget={data.dashboard.previewWidgetFlourishPraiseIntegration}
            storecastId={props.route.params.storecastId}
            interactive={false}
            mode="IDLE"
          />
        ) : null}
      </WASizePreview>
    </View>
  );
}
